import {
  useGetExamEventByCardId,
  useGetExamsByIds,
  type GetExamsByIdsQuery,
} from '@exam-room/graphql';
import { adjustTime } from '@shared/utils';
import { createContext, useContext, useMemo, useEffect, useState } from 'react';

import { useExamCard } from '../exam-card';
import { useNetwork } from '../network';

import { type Exam, type ExamEvent } from './types';
import { useExaminationTime } from './useExaminationTime';

interface ExamDuration {
  eventOrder: number;
  extendedDurationInMs: number;
  postponedDurationInMs: number;
  totalManualExtendedDurationInMs: number;
}

interface ExamEventContextType {
  examEvent: ExamEvent;
  loading: boolean;
  error: Error | undefined;
  closestExam: Exam | null;
  examsData: GetExamsByIdsQuery['erGetExamsByIds'];
  isLoadingExams: boolean;
  examDuration: ExamDuration;
  examDurationError: Error | undefined;
}

interface ExamEventProviderProps {
  children: React.ReactNode;
}

const ExamEventContext = createContext<ExamEventContextType | undefined>(undefined);

function ExamEventProvider({ children }: Readonly<ExamEventProviderProps>) {
  const { isOnline } = useNetwork();
  const { loading: isExamCardLoading, examCard } = useExamCard();

  const {
    examinationTime,
    currentExaminationTime,
    loading: isTimeLoading,
    error: timeError,
  } = useExaminationTime({
    examCardId: examCard?.id,
    skip: isExamCardLoading,
  });

  const {
    data,
    loading: isLoading,
    error,
  } = useGetExamEventByCardId({
    variables: {
      cardId: examCard?.id ?? '',
    },
    skip: !examCard?.id || isExamCardLoading || !isOnline,
  });

  const eventOrder = currentExaminationTime?.order ?? 0;
  const extendedDurationInMs = currentExaminationTime?.extendedDurationInMs ?? 0;
  const postponedDurationInMs = currentExaminationTime?.postponedDurationInMs ?? 0;
  const totalManualExtendedDurationInMs =
    currentExaminationTime?.totalManualExtendedDurationInMs ?? 0;

  const adjustedExamEvent = useMemo(() => {
    if (!data?.erGetExamEventByCardId || !examinationTime) return null;

    const examEvent = { ...data.erGetExamEventByCardId };

    examEvent.metadata = {
      ...examEvent.metadata,
      events: examEvent.metadata.events.map((event: Exam) => {
        const adjustedEvent = { ...event };
        const eventTime = examinationTime?.find((time) => time.order === event.order);
        const postponedDurationInMs = eventTime?.postponedDurationInMs ?? 0;

        adjustedEvent.startedAt = adjustTime(
          event.startedAt,
          eventTime?.postponedDurationInMs ?? 0,
        );
        adjustedEvent.endedAt = adjustTime(
          event.endedAt,
          postponedDurationInMs + extendedDurationInMs,
        );

        // Also provide global adjusted time (only exam event time adjustment) for displaying in exam instruction page
        adjustedEvent.globalAdjustedStartDate = adjustTime(
          event.startedAt,
          event.postponeDurationInMs ?? 0,
        );
        adjustedEvent.globalAdjustedEndDate = adjustTime(
          event.endedAt,
          event.postponeDurationInMs ?? 0,
        );

        adjustedEvent.originalStartedAt = event.startedAt;
        adjustedEvent.originalEndedAt = event.endedAt;

        return adjustedEvent;
      }),
    };

    return examEvent;
  }, [data, extendedDurationInMs, examinationTime]);

  const [cachedExamEvent, setCachedExamEvent] = useState<ExamEvent | null>(null);

  useEffect(() => {
    if (adjustedExamEvent) {
      setCachedExamEvent(adjustedExamEvent);
    }
  }, [adjustedExamEvent]);

  const effectiveExamEvent = adjustedExamEvent ?? cachedExamEvent;

  const closestExam: Exam | null = useMemo(() => {
    const currentEvent = (
      (effectiveExamEvent?.metadata?.events ?? []) as ExamEvent['metadata']['events']
    ).find((event) => event.order === eventOrder);

    if (!currentEvent) return null;

    return {
      ...currentEvent,
      startedAt: new Date(currentEvent.startedAt),
      endedAt: new Date(currentEvent.endedAt),
    } as Exam;
  }, [effectiveExamEvent, eventOrder]);

  const examIds = (effectiveExamEvent?.metadata?.events ?? []).map(
    (event: { examId: string }) => event.examId,
  );

  const { data: examsQueryData, loading: isLoadingExams } = useGetExamsByIds({
    variables: {
      examIds,
    },
    skip: examIds.length < 1,
  });

  const contextValue = useMemo(
    () => ({
      examEvent: effectiveExamEvent as ExamEvent, // NOTE: We cast because we'll handle the undefined case in the layout
      loading: isLoading || isTimeLoading,
      error,
      closestExam,
      examsData: examsQueryData?.erGetExamsByIds ?? [],
      isLoadingExams,
      examDuration: {
        eventOrder,
        extendedDurationInMs,
        postponedDurationInMs,
        totalManualExtendedDurationInMs,
      },
      examDurationError: timeError,
    }),
    [
      effectiveExamEvent,
      isLoading,
      isLoadingExams,
      closestExam,
      error,
      examsQueryData,
      timeError,
      eventOrder,
      extendedDurationInMs,
      postponedDurationInMs,
      totalManualExtendedDurationInMs,
    ],
  );

  return <ExamEventContext.Provider value={contextValue}>{children}</ExamEventContext.Provider>;
}

function useExamEvent() {
  const context = useContext(ExamEventContext);

  if (context === undefined) {
    throw new Error('useExamEvent must be used within ExamEventContext');
  }

  return context;
}

export { ExamEventProvider, useExamEvent, ExamEventContext };
export type { ExamEventContextType, ExamEventProviderProps, ExamEvent };
